import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { GIButton, SideNav } from 'app-components';

import { USER_PROFILE } from '@constants/common/statusTypes';
import { configs } from '@constants/configs/configs';
import { PAGE_TYPE } from '@constants/crm/displayPageType';
import { useAccountSettings } from '@hooks/index';
import useKeyCloakStatus from '@hooks/useKeyCloakStatus';
import useDisclosure from '@hooks/useDisclosure';

import { ConditionalRendering } from '../conditional-rendering';
import AccountActivationEmailModal from '../modals/AccountActivationEmailModal';
import ResetPasswordModal from '../modals/ResetPasswordModal';
import UnblockUserModal from '../modals/UnblockUserModal';
import ViewMemberAccountModal from '../modals/ViewMemberAccountModal';
import Tooltip from '../tooltip/Tooltip';
import useResetPasswordFlow from '../../../hooks/useResetPasswordFlow';

const StyleEmailContainer = styled.span`
    .tooltip-base {
        text-overflow: ellipsis;
        overflow: hidden;
        border-bottom: none;
        padding-left: 0 !important;
        padding-right: 0 !important;
    }
    .tooltip-base:hover {
        background-color: transparent !important;
    }
    .tooltip-component {
        max-width: fit-content;
    }

`;

const StylePendingUserStatus = styled.div`
    text-align: center;

    .pending-user-status {
        color: ${({ theme }) => theme.primaryMain};
        margin-top:10px;
        margin-bottom: 0;
        font-size: ${({ theme }) => theme.fontSizeBody};
    }

    li {
        padding-top: 0;
        text-align: center;
    }

    svg path {
        fill: ${({ theme }) => theme.primaryMain} !important;
    }

    div {
        justify-content: center;
    }
`;

export interface PermissionMap {
    memberEditDob: string;
    memberViewLatestAccountNote: string;
    memberEditRivs: string;
    memberViewViewCase: string;
    memberViewSendAccountActivationEmail: string;
    memberEditFirstName: string;
    memberEdit: string;
    memberViewApplications: string;
    memberViewAccessCode: string;
    memberViewMnsureId: string;
    memberViewCreateTkt: string;
    memberViewBackToConsumerHome: string;
    memberViewBasicInfo: string;
    memberViewEnrollments: string;
    memberViewViewMemAcct: string;
    memberViewCaseid: string;
    memberEditUnemploymentInsurancePremium: string;
    memberEditLastName: string;
    memberViewInboxLink: string;
    memberViewHistory: string;
    memberViewAdminSearch: string;
    memberViewResetPassword: string;
    memberViewComments: string;
    memberViewTicketHistory: string;
    memberEditSsn: string;
    memberViewAppealHistory: string;
    memberViewReactivateAccount: string;
}
export interface ExtProps {
    hhUserId: string;
    hhFirstName: string;
    hhLastName: string;
    hhId: string;
    hhUser: string;
    encHHId: string;
    encUserId?: string;
    calheersEnv: string;
    addNewViewCaseUrl: string;
    checkDilog: string;
    permissionMap: PermissionMap | null;
    idmUserStatus?: string | null;
    userStatus?: string | null;
    switchToModuleName: string;
    activeModuleName: string;
    hhEmail: string;
    hhPhone: string;
    hhUserStatus: string;
    referenceId?: string;
}

// props
export interface AdminMemberSideNavProps {
    capTicketEnabled: boolean;
    extProps: ExtProps | null;
    unreadMessageCount: number;
    page: string;
    environment: any;
    updateUnblockResponse?: () => void;
}

function AdminMemberSideNav({
    page,
    extProps,
    capTicketEnabled,
    unreadMessageCount,
    environment,
    updateUnblockResponse,
}: AdminMemberSideNavProps) {
    const {
        isOpen: isOpenViewMemberAccountModal,
        onOpen: openViewMemberAccountModal,
        onClose: closeViewMemberAccountModal,
    } = useDisclosure();
    const {
        isOpen: isOpenAccountActivationEmailModal,
        onOpen: openAccountActivationEmailModal,
        onClose: closeAccountActivationEmailModal,
    } = useDisclosure();
    const { isOpen: isOpenUnblockUser, onOpen: openUnblockUserModal, onClose: closeUnblockUserModal } = useDisclosure();
    const {
        isOpen: isOpenResetPasswordModal,
        onOpen: openResetPasswordModal,
        onClose: closeResetPasswordModal,
    } = useDisclosure();
    const { unblockUserResponse, resetPwdResponse, resetPassword, resetPassowrdAlternateEmail, unblockUser } =
        useAccountSettings();
    const intl = useIntl();
    const navigate = useNavigate();
    const { isIDResetPasswordFlow } = useResetPasswordFlow();
    const isMNState = environment?.isState?.MN;
    const isMNFullStack = environment?.isMNFullStack;
    const shouldFetchKeyCloak = !!extProps?.encUserId && (!isMNState || !!isMNFullStack);
    const useKeyCloakForDisplay = !isMNState || !!isMNFullStack;
    const { effectiveUserStatus, effectiveIdmUserStatus } = useKeyCloakStatus({
        encryptedUserId: extProps?.encUserId,
        shouldFetch: shouldFetchKeyCloak,
        useKeyCloakForDisplay,
        userStatus: extProps?.userStatus,
        idmUserStatus: extProps?.idmUserStatus,
    });

    if (!extProps) return null;

    const {
        userStatus,
        idmUserStatus,
        hhUserId,
        hhFirstName,
        hhLastName,
        encHHId,
        encUserId,
        hhId,
        hhUser,
        calheersEnv,
        addNewViewCaseUrl,
        checkDilog,
        permissionMap,
        switchToModuleName,
        activeModuleName,
        hhEmail,
        hhPhone,
        hhUserStatus,
        referenceId = '',
    } = extProps;

    const {
        memberViewViewCase,
        memberViewSendAccountActivationEmail,
        memberViewApplications,
        memberViewCreateTkt,
        memberViewBackToConsumerHome,
        memberViewBasicInfo,
        memberViewEnrollments,
        memberViewInboxLink,
        memberViewHistory,
        memberViewAdminSearch,
        memberViewResetPassword,
        memberViewComments,
        memberViewTicketHistory,
        memberViewAppealHistory,
        memberViewReactivateAccount,
        memberViewViewMemAcct,
    } = permissionMap || ({} as PermissionMap);
    const isShowUserEmail = memberViewResetPassword == 'true' || memberViewSendAccountActivationEmail == 'true';

    useEffect(() => {
        if (unblockUserResponse) {
            closeUnblockUserModal();
            if (updateUnblockResponse) {
                updateUnblockResponse();
            }
        }
    }, [unblockUserResponse]);

    useEffect(() => {
        if (resetPwdResponse?.OVERRIDE_EMAIL === true) {
            openResetPasswordModal();
        }
    }, [resetPwdResponse]);

    const onHandleViewMemberAccountLink = async () => {
        // start impersonation mode
        sessionStorage.removeItem('exitImpersonation'); 
        sessionStorage.isInImpersonationMode = true;
        sessionStorage.impersonateHHID = hhId;

        // redirect as
        const checkConsumerView = 'on';
        const switchToResourceName = encodeURIComponent(`${hhFirstName} ${hhLastName}`);
        const url = `/hix/crm/consumer/dashboard?checkConsumerView=${checkConsumerView}&switchToModuleName=${switchToModuleName}&switchToModuleId=${encHHId}&switchToResourceName=${switchToResourceName}`;
        window.location.href = url;
    };

    const onHandleViewMemberAccount = () => {
        const isShowViewMemberAccountModal = !checkDilog || checkDilog === 'off';

        // storing switchModuleName in localStorage for HIX-198456
        localStorage.setItem('lcSwitchModuleName', switchToModuleName);
        localStorage.setItem('lcHhEmail', hhEmail);

        if (isShowViewMemberAccountModal) {
            openViewMemberAccountModal();
        } else {
            onHandleViewMemberAccountLink();
        }
    };

    const handleResetPasswordClick = () => {
        if (environment.isState.ID) {
            const url = `${configs.routes.RESET_PASSWORD}?hhid=${encHHId}`;
            navigate(url, { replace: true });
        } else {
            resetPassword(hhId);
        }
    };

    const onSubmitPasswordAlternateEmail = (hhid: string, data: any) => {
        resetPassowrdAlternateEmail(hhid, data);
        closeResetPasswordModal();
    };
    const handleUnblockUserClick = () => {
        openUnblockUserModal();
    };

    const unblockUserCall = () => {
        unblockUser(hhId, idmUserStatus);
    };

    const isResetPassword =
        effectiveIdmUserStatus !== USER_PROFILE.LOCKED &&
        effectiveUserStatus === USER_PROFILE.ACTIVE &&
        !isIDResetPasswordFlow;
    const isReactiveAccount =
        effectiveIdmUserStatus !== USER_PROFILE.LOCKED && effectiveUserStatus === USER_PROFILE.INACTIVE_DORMANT;
    const isUnlockAccount =
        effectiveUserStatus !== USER_PROFILE.INACTIVE && effectiveIdmUserStatus === USER_PROFILE.LOCKED;
    const isUserInactive = effectiveUserStatus === USER_PROFILE.INACTIVE;
    const isPendingActivation =
        memberViewSendAccountActivationEmail === 'true' &&
        effectiveUserStatus !== null &&
        effectiveUserStatus === USER_PROFILE.PENDING;
    const isStandaloneActivationEmailLinkVisible =
        memberViewSendAccountActivationEmail === 'true' && !hhUser && !isPendingActivation;

    const handleInternalNavigation = url => navigate(url);

    const permissionKeys = [
        'memberViewBackToConsumerHome',
        'memberViewAdminSearch',
        'memberViewViewCase',
        'memberViewViewMemAcct',
        'memberViewCreateTkt',
    ];

    const hasPermission = permissionMap && permissionKeys.some(key => permissionMap[key] === 'true');
    const canViewActions = !!hasPermission;

    return (
        <>
            {isOpenResetPasswordModal && (
                <ResetPasswordModal
                    hhId={hhId}
                    onClose={closeResetPasswordModal}
                    onClickHandler={onSubmitPasswordAlternateEmail}
                />
            )}
            {isOpenUnblockUser && <UnblockUserModal onContinue={unblockUserCall} onClose={closeUnblockUserModal} />}
            {isOpenViewMemberAccountModal && (
                <ViewMemberAccountModal
                    encHHId={encHHId}
                    hhId={hhId}
                    hhFirstName={hhFirstName}
                    hhLastName={hhLastName}
                    switchToModuleName={switchToModuleName}
                    onClose={closeViewMemberAccountModal}
                />
            )}
            {isOpenAccountActivationEmailModal && (
                <AccountActivationEmailModal
                    hhId={hhId}
                    hhEmail={hhEmail}
                    hhPhone={hhPhone}
                    encHHId={encHHId}
                    onClose={closeAccountActivationEmailModal}
                />
            )}
            <SideNav>
                <SideNav.Header labelId="common.about_customer" />
                <SideNav.Item>
                    {memberViewBasicInfo === 'true' && (
                        <SideNav.Link
                            labelId="admin.member.view.side.nav.basic_information"
                            name="basic-information"
                            url={`/hix/crm/member/viewmember/${encHHId}`}
                        />
                    )}
                    {memberViewApplications === 'true' && (
                        <SideNav.ActionLink
                            isSelectedDefault={page === PAGE_TYPE.APPLICATION_ELIGIBILITY_PAGE}
                            labelId="admin.member.view.side.nav.application"
                            onClickHandler={() =>
                                handleInternalNavigation(`${configs.routes.MEMBER_APP_LIST}/${encHHId}`)
                            }
                        />
                    )}
                    {memberViewEnrollments === 'true' && (
                        <SideNav.ActionLink
                            isSelectedDefault={page === PAGE_TYPE.ENROLLMENTS}
                            labelId="admin.member.view.side.nav.enrollments"
                            onClickHandler={() =>
                                handleInternalNavigation(configs.routes.MEMBER_ENROLLMENT_LIST.replace(':id', encHHId))
                            }
                        />
                    )}
                    <ConditionalRendering
                        isTrue={
                            memberViewBasicInfo === 'true' &&
                            Boolean(environment?.showHistoricalLinking) &&
                            Boolean(referenceId)
                        }
                    >
                        <SideNav.ActionLink
                            isSelectedDefault={page === PAGE_TYPE.HISTORICAL_DOCUMENTS}
                            labelId="admin.member.view.side.nav.historicalDocuments"
                            onClickHandler={() =>
                                handleInternalNavigation(
                                    `${configs.routes.CAP_MEMBER_HISTORICAL_DOCUMENTS}/${encHHId}?referenceId=${encodeURIComponent(referenceId)}`,
                                )
                            }
                        />
                        <SideNav.ActionLink
                            isSelectedDefault={page === PAGE_TYPE.HISTORICAL_NOTICES}
                            labelId="admin.member.view.side.nav.historicalNotices"
                            onClickHandler={() =>
                                handleInternalNavigation(
                                    `${configs.routes.CAP_MEMBER_HISTORICAL_NOTICES}/${encHHId}?referenceId=${encodeURIComponent(referenceId)}`,
                                )
                            }
                        />
                    </ConditionalRendering>
                    {memberViewComments === 'true' && (
                        <SideNav.Link
                            labelId="admin.member.view.side.nav.account_notes_history"
                            name="comments"
                            url={`/hix/crm/member/comments/${encHHId}`}
                        />
                    )}
                    <ConditionalRendering isTrue={memberViewInboxLink === 'true' && memberViewHistory === 'true'}>
                        <SideNav.ActionLink
                            isSelectedDefault={page === PAGE_TYPE.SECURE_INBOX_PAGE}
                            labelId={`${intl.formatMessage({ id: 'common.inbox' })} (${unreadMessageCount})`}
                            onClickHandler={() =>
                                handleInternalNavigation(`${configs.routes.SECURE_INBOX}?hhid=${encHHId}`)
                            }
                        />
                    </ConditionalRendering>

                    {memberViewHistory === 'true' && (
                        <SideNav.ActionLink
                            isSelectedDefault={page === PAGE_TYPE.ACCOUNT_ACTIVITY}
                            labelId="admin.member.view.side.nav.account_activity"
                            onClickHandler={() =>
                                handleInternalNavigation(configs.routes.ACCOUNT_ACTIVITY.replace(':id', encHHId))
                            }
                        />
                    )}
                    {memberViewAppealHistory === 'true' && (
                        <SideNav.Link
                            labelId="admin.member.view.side.nav.appeal_history"
                            name="appeal-history"
                            url={`/hix/crm/member/appealhistory/${encHHId}`}
                        />
                    )}
                    {memberViewTicketHistory === 'true' && (
                        <SideNav.Link
                            labelId="admin.member.view.side.nav.ticket_history"
                            name="ticket-history"
                            url={`/hix/crm/member/tickethistory/${encHHId}`}
                        />
                    )}
                </SideNav.Item>
                <>
                    <ConditionalRendering isTrue={canViewActions}>
                        <SideNav.Header labelId="common.actions" iconName="cog" />
                    </ConditionalRendering>
                    <SideNav.Item hasIcon={true}>
                        <ConditionalRendering isTrue={canViewActions}>
                            <>
                                {memberViewBackToConsumerHome === 'true' && (
                                    <SideNav.Link
                                        labelId="admin.member.view.side.nav.back_to_consumer_home"
                                        name="back-to-consumer-home"
                                        url={`https://${calheersEnv}/static/lw-web/account-home`}
                                    />
                                )}
                                {memberViewAdminSearch === 'true' && (
                                    <SideNav.Link
                                        labelId="admin.member.view.side.nav.admin_search"
                                        name="admin-search"
                                        url={`https://${calheersEnv}/apspahbx/ahbxadmin.portal?_nfpb=true&_st=&_nfls=false&_pageLabel=administrationHomePage#`}
                                    />
                                )}
                                {memberViewViewCase === 'true' && (
                                    <SideNav.Link
                                        labelId="admin.member.view.side.nav.view_case"
                                        name="view-case"
                                        url={`${addNewViewCaseUrl}`}
                                    />
                                )}
                                {memberViewViewMemAcct === 'true' && (
                                    <SideNav.ActionLink
                                        iconName="eye"
                                        labelId="admin.member.view.side.nav.view_member_account"
                                        onClickHandler={onHandleViewMemberAccount}
                                    />
                                )}
                                {memberViewCreateTkt === 'true' && capTicketEnabled && (
                                    <SideNav.Link
                                        labelId="admin.member.view.side.nav.create_ticket"
                                        name="create-ticket"
                                        url={`/hix/ticketmgmt/ticket/createpage?prefillUser=true&roleName=INDIVIDUAL&moduleId=${hhId}&requestorId=${hhUserId}&requestorName=${hhFirstName} ${hhLastName}&moduleName=HOUSEHOLD`}
                                    />
                                )}
                                <ConditionalRendering isTrue={isShowUserEmail && !!hhEmail}>
                                    <li>
                                        <p className="text-center margin-t-10">
                                            <StyleEmailContainer>
                                                <Tooltip
                                                    id={hhEmail}
                                                    elementId="email_id_viewer"
                                                    content={hhEmail}
                                                    place="top"
                                                />
                                            </StyleEmailContainer>
                                        </p>
                                    </li>
                                </ConditionalRendering>
                                <ConditionalRendering isTrue={isStandaloneActivationEmailLinkVisible}>
                                    <SideNav.ActionLink
                                        iconName="envelope"
                                        labelId="admin.member.view.side.nav.send_account_activation_email"
                                        onClickHandler={openAccountActivationEmailModal}
                                    />
                                </ConditionalRendering>
                            </>
                        </ConditionalRendering>
                        {isUserInactive && (
                            <li id="inactiveUser" className="text-center">
                                <div className="margin-b-20-px">{effectiveUserStatus || userStatus}</div>
                                {memberViewReactivateAccount == 'true' && (
                                    <GIButton
                                        type="button"
                                        variant="secondary"
                                        id="reactivateInactive"
                                        onClickHandler={handleUnblockUserClick}
                                        labelId={'admin.member.view.side.nav.reactivate_account'}
                                    />
                                )}
                            </li>
                        )}
                        {isUnlockAccount && (
                            <li id="lockedUser" className="text-center">
                                <div className="margin-b-20-px">{effectiveIdmUserStatus || idmUserStatus}</div>
                                {memberViewReactivateAccount == 'true' && (
                                    <GIButton
                                        type="button"
                                        variant="secondary"
                                        id="unlockUser"
                                        onClickHandler={unblockUserCall}
                                        labelId={'admin.member.view.side.nav.unlock_account'}
                                    />
                                )}
                            </li>
                        )}
                        {isReactiveAccount && (
                            <li id="inactiveDormantAccUser" className="text-center">
                                <div className="margin-b-20-px text-danger">{effectiveUserStatus || userStatus}</div>
                                {memberViewReactivateAccount == 'true' && (
                                    <GIButton
                                        type="button"
                                        variant="secondary"
                                        id="reactiveAccount"
                                        onClickHandler={handleUnblockUserClick}
                                        labelId={'admin.member.view.side.nav.reactivate_account'}
                                    />
                                )}
                            </li>
                        )}
                        {isResetPassword && (
                            <li id="activeUser" className="text-center">
                                <div className="margin-b-10">{effectiveUserStatus || userStatus}</div>
                                {memberViewResetPassword == 'true' && (
                                    <GIButton
                                        type="button"
                                        variant="secondary"
                                        id="resetPassword"
                                        onClickHandler={handleResetPasswordClick}
                                        labelId={'admin.member.view.side.nav.reset_pwd'}
                                    />
                                )}
                            </li>
                        )}

                        <ConditionalRendering isTrue={isPendingActivation}>
                            <StylePendingUserStatus data-testid="pending-activation-section">
                                <div className='pending-user-status' data-testid="pending-user-status">{effectiveUserStatus || userStatus}</div>
                                <SideNav.ActionLink
                                    iconName="envelope"
                                    labelId="admin.member.view.side.nav.send_account_activation_email"
                                    onClickHandler={openAccountActivationEmailModal}
                                    data-testid="send-activation-email-link"
                                >
                                </SideNav.ActionLink>
                            </StylePendingUserStatus>
                        </ConditionalRendering>
                    </SideNav.Item>
                </>
            </SideNav>
        </>
    );
}

export default AdminMemberSideNav;
